import React, { useState } from 'react';
import Button from '../Button';
import FormInput from '../FormInput';
import Tick from '../../icons/rev_confirm.png';
import useIsMobile from '../../hooks/useIsMobile';
import '../../containers/Hedge/hedge.css';

/** OEM companies we hedge with (not converter part-number OEMs). */
const HEDGE_PARTNER_OPTIONS = ['BASF', 'StillWater (STW)'].map((name) => ({
  id: name,
  value: name,
}));

const HedgeAddEdit = ({ item, onClose, onSave, type, isLoading = false }) => {
  const isMobile = useIsMobile();
  const [oemName, setOemName] = useState(item?.oemName || '');
  const [hedgeDate, setHedgeDate] = useState(item?.hedgeDate || '');
  const [abcatLotId, setAbcatLotId] = useState(item?.abcatLotId || '');
  const [oemLotId, setOemLotId] = useState(item?.oemLotId || '');
  const [ptOunces, setPtOunces] = useState(item?.ptOunces || '');
  const [ptPricePerOz, setPtPricePerOz] = useState(item?.ptPricePerOz || '');
  const [pdOunces, setPdOunces] = useState(item?.pdOunces || '');
  const [pdPricePerOz, setPdPricePerOz] = useState(item?.pdPricePerOz || '');
  const [rhOunces, setRhOunces] = useState(item?.rhOunces || '');
  const [rhPricePerOz, setRhPricePerOz] = useState(item?.rhPricePerOz || '');
  const [notes, setNotes] = useState(item?.notes || '');
  const oemOptions = HEDGE_PARTNER_OPTIONS;
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [validations, setValidations] = useState({});

  const validateFields = () => {
    const updatedValidations = { ...validations };
    updatedValidations.oemName = oemName && oemName.trim().length > 0;
    updatedValidations.hedgeDate = hedgeDate && hedgeDate.trim().length > 0;
    updatedValidations.ptOunces = ptOunces !== '' && !isNaN(parseFloat(ptOunces)) && parseFloat(ptOunces) >= 0;
    updatedValidations.ptPricePerOz = ptPricePerOz === '' || (!isNaN(parseFloat(ptPricePerOz)) && parseFloat(ptPricePerOz) >= 0);
    updatedValidations.pdOunces = pdOunces !== '' && !isNaN(parseFloat(pdOunces)) && parseFloat(pdOunces) >= 0;
    updatedValidations.pdPricePerOz = pdPricePerOz === '' || (!isNaN(parseFloat(pdPricePerOz)) && parseFloat(pdPricePerOz) >= 0);
    updatedValidations.rhOunces = rhOunces !== '' && !isNaN(parseFloat(rhOunces)) && parseFloat(rhOunces) >= 0;
    updatedValidations.rhPricePerOz = rhPricePerOz === '' || (!isNaN(parseFloat(rhPricePerOz)) && parseFloat(rhPricePerOz) >= 0);

    setValidations(updatedValidations);
    return updatedValidations;
  };

  const handleSave = () => {
    setHasSubmitted(true);
    const updatedValidations = validateFields();

    if (
      !updatedValidations.oemName ||
      !updatedValidations.hedgeDate ||
      (!updatedValidations.ptOunces && !updatedValidations.pdOunces && !updatedValidations.rhOunces)
    ) {
      return;
    }

    const hedgeData = {
      hedgeId: item?.hedgeId,
      oemName: oemName.trim(),
      hedgeDate: hedgeDate,
      abcatLotId: abcatLotId.trim(),
      oemLotId: oemLotId.trim(),
      ptOunces: ptOunces ? parseFloat(ptOunces) : 0,
      ptPricePerOz: ptPricePerOz ? parseFloat(ptPricePerOz) : 0,
      pdOunces: pdOunces ? parseFloat(pdOunces) : 0,
      pdPricePerOz: pdPricePerOz ? parseFloat(pdPricePerOz) : 0,
      rhOunces: rhOunces ? parseFloat(rhOunces) : 0,
      rhPricePerOz: rhPricePerOz ? parseFloat(rhPricePerOz) : 0,
      notes: notes.trim(),
    };

    onSave(hedgeData);
  };

  return (
    <div
      style={{
        position: 'relative',
        padding: '10px',
      }}
    >

      <div className="hedge-addedit-fields">
        <div className="hedge-addedit-field">
          <div className="hedge-addedit-label">
            Hedge partner <span style={{ color: 'red' }}>*</span>
          </div>
          <select
            className={`form-control rounded-pill font-primary fs-14 fRegular converter-formInput bg-blue wt-100p hedge-addedit-select${hasSubmitted && !validations.oemName ? ' hedge-addedit-select--error' : ''}`}
            value={oemName}
            onChange={(e) => setOemName(e.target.value)}
          >
            <option value="">Select company</option>
            {oemName && !oemOptions.some((o) => o.value === oemName) && (
              <option value={oemName}>{oemName}</option>
            )}
            {oemOptions.map((oem) => (
              <option key={oem.id} value={oem.value}>
                {oem.value}
              </option>
            ))}
          </select>
          {hasSubmitted && !validations.oemName && (
            <div className="fs-10 c-error fw-bold mt-4">Please select a hedge partner</div>
          )}
        </div>

        <div className="hedge-addedit-field">
          <div className="hedge-addedit-label">
            Hedge Date <span style={{ color: 'red' }}>*</span>
          </div>
          <FormInput
            inputClassName="bg-blue rounded-pill"
            style={{ paddingLeft: '10px' }}
            type="date"
            value={hedgeDate}
            onChange={(e) => setHedgeDate(e.target.value)}
            hasError={hasSubmitted && !validations.hedgeDate}
          />
          {hasSubmitted && !validations.hedgeDate && (
            <div className="fs-10 c-error fw-bold mt-4">Please select a date</div>
          )}
        </div>

        <div className="hedge-addedit-field hedge-addedit-field--inline">
          <div className="hedge-addedit-label">ABCAT LOT ID</div>
          <FormInput
            className="wt-100p"
            inputClassName="bg-blue rounded-pill"
            style={{ paddingLeft: '10px' }}
            inputPlaceHolder="e.g. STW406"
            value={abcatLotId}
            onChange={(e) => setAbcatLotId(e.target.value)}
          />
        </div>

        <div className="hedge-addedit-field hedge-addedit-field--inline">
          <div className="hedge-addedit-label">OEM LOT ID</div>
          <FormInput
            className="wt-100p"
            inputClassName="bg-blue rounded-pill"
            style={{ paddingLeft: '10px' }}
            inputPlaceHolder="e.g. AV00145"
            value={oemLotId}
            onChange={(e) => setOemLotId(e.target.value)}
          />
        </div>
      </div>

      {/* Metal Ounces and Prices */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#212529', marginBottom: '16px' }}>
          Metal Ounces & Prices (Future Contract Prices)
        </div>

        {/* Platinum Section */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
            Platinum (PT)
          </div>
          <div className={`flex flex-between flex-middle flex-wrap ${isMobile ? 'flex-column' : ''}`}>
            <div className={`${!isMobile ? 'wt-48p' : 'wt-100p mb-12'}`}>
              <div className={`c-gray fs-13 ${isMobile ? 'mb-2' : 'mb-4'}`}>Ounces</div>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{ paddingLeft: '10px' }}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={ptOunces}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                    setPtOunces(value);
                  }
                }}
                hasError={hasSubmitted && ptOunces !== '' && !validations.ptOunces}
              />
            </div>
            <div className={`${!isMobile ? 'wt-48p' : 'wt-100p'}`}>
              <div className={`c-gray fs-13 ${isMobile ? 'mb-2' : 'mb-4'}`}>
                Price per Ounce ($)
              </div>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{ paddingLeft: '10px' }}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={ptPricePerOz}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                    setPtPricePerOz(value);
                  }
                }}
                hasError={hasSubmitted && ptPricePerOz !== '' && !validations.ptPricePerOz}
              />
            </div>
          </div>
          {ptOunces && ptPricePerOz && parseFloat(ptOunces) > 0 && parseFloat(ptPricePerOz) > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#28a745', fontWeight: '600' }}>
              Total Value: ${(parseFloat(ptOunces) * parseFloat(ptPricePerOz)).toFixed(2)}
            </div>
          )}
        </div>

        {/* Palladium Section */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
            Palladium (PD)
          </div>
          <div className={`flex flex-between flex-middle flex-wrap ${isMobile ? 'flex-column' : ''}`}>
            <div className={`${!isMobile ? 'wt-48p' : 'wt-100p mb-12'}`}>
              <div className={`c-gray fs-13 ${isMobile ? 'mb-2' : 'mb-4'}`}>Ounces</div>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{ paddingLeft: '10px' }}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={pdOunces}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                    setPdOunces(value);
                  }
                }}
                hasError={hasSubmitted && pdOunces !== '' && !validations.pdOunces}
              />
            </div>
            <div className={`${!isMobile ? 'wt-48p' : 'wt-100p'}`}>
              <div className={`c-gray fs-13 ${isMobile ? 'mb-2' : 'mb-4'}`}>
                Price per Ounce ($)
              </div>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{ paddingLeft: '10px' }}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={pdPricePerOz}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                    setPdPricePerOz(value);
                  }
                }}
                hasError={hasSubmitted && pdPricePerOz !== '' && !validations.pdPricePerOz}
              />
            </div>
          </div>
          {pdOunces && pdPricePerOz && parseFloat(pdOunces) > 0 && parseFloat(pdPricePerOz) > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#28a745', fontWeight: '600' }}>
              Total Value: ${(parseFloat(pdOunces) * parseFloat(pdPricePerOz)).toFixed(2)}
            </div>
          )}
        </div>

        {/* Rhodium Section */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
            Rhodium (RH)
          </div>
          <div className={`flex flex-between flex-middle flex-wrap ${isMobile ? 'flex-column' : ''}`}>
            <div className={`${!isMobile ? 'wt-48p' : 'wt-100p mb-12'}`}>
              <div className={`c-gray fs-13 ${isMobile ? 'mb-2' : 'mb-4'}`}>Ounces</div>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{ paddingLeft: '10px' }}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={rhOunces}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                    setRhOunces(value);
                  }
                }}
                hasError={hasSubmitted && rhOunces !== '' && !validations.rhOunces}
              />
            </div>
            <div className={`${!isMobile ? 'wt-48p' : 'wt-100p'}`}>
              <div className={`c-gray fs-13 ${isMobile ? 'mb-2' : 'mb-4'}`}>
                Price per Ounce ($)
              </div>
              <FormInput
                inputClassName="bg-blue rounded-pill"
                style={{ paddingLeft: '10px' }}
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={rhPricePerOz}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                    setRhPricePerOz(value);
                  }
                }}
                hasError={hasSubmitted && rhPricePerOz !== '' && !validations.rhPricePerOz}
              />
            </div>
          </div>
          {rhOunces && rhPricePerOz && parseFloat(rhOunces) > 0 && parseFloat(rhPricePerOz) > 0 && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#28a745', fontWeight: '600' }}>
              Total Value: ${(parseFloat(rhOunces) * parseFloat(rhPricePerOz)).toFixed(2)}
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className={`${isMobile ? 'mb-10' : 'mb-40'}`}>
        <div className={`c-gray fs-14 ${isMobile ? 'mb-2' : 'mb-4'}`}>Notes</div>
        <FormInput
          inputClassName="bg-blue rounded-pill"
          style={{ paddingLeft: '10px' }}
          placeholder="Additional notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-middle flex-right">
        <Button
          className="flex ht-40 flex-middle mr-20 btn btn-red"
          type="modalCancel"
          onClick={onClose}
        >
          <span>CANCEL</span>
        </Button>

        <Button
          type="modalSave"
          className="btn ht-40"
          onClick={handleSave}
          disabled={isLoading}
        >
          <img src={Tick} className="wt-20 ht-20 mr-4" />
          <span>{isLoading ? 'Saving...' : 'SAVE'}</span>
        </Button>
      </div>
    </div>
  );
};

export default HedgeAddEdit;
